import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Book } from '@/utils/bookRouting';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { analytics } from '@/services/analytics';
import { bookAnalytics, type BookVisibilityTrigger } from '@/services/bookAnalytics';
import TopNavigation from '../components/TopNavigation';
import LoginRequiredState from '../components/LoginRequiredState';
import { BookGroup, BookConversations } from '@/components/profile/BookGroup';
import {
  getAudioProgressForBook,
  getEffectiveProgress,
  saveAudioProgress,
  saveBookFavourite,
  saveBookHidden,
  isBookInProgress,
  isBookFinished,
} from '@/utils/audioProgressUtils';
import { AudioPosition } from '@/types/audioPosition';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import MyBookItem from '../components/MyBookItem';

type FilterType = 'All' | 'Finished' | 'Unfinished' | 'Favourites' | 'Hidden';

interface FilterButtonProps {
  label: string;
  value: FilterType;
  activeFilter: FilterType;
  isDarkMode: boolean;
  onClick: (value: FilterType) => void;
}

const FilterButton: React.FC<FilterButtonProps> = React.memo(({ label, value, activeFilter, isDarkMode, onClick }) => {
  return (
    <button
      onClick={() => onClick(value)}
      className={`
        px-2.5 2xs:px-4 py-1.5 rounded-full text-xs 2xs:text-sm font-medium cursor-pointer transition-all duration-200
        ${activeFilter === value
          ? 'bg-blue-600 text-white shadow-md'
          : isDarkMode
            ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
            : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
        }
      `}
    >
      {label}
    </button>
  );
});

interface MyBooksPageProps {
  books: Book[];
  onNavigateHome: () => void;
  onNavigateToExplore: () => void;
  onNavigateToLibrary: () => void;
  onNavigateToMyBooks: () => void;
  onNavigateToProfile: () => void;
  onNavigateToLogin: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
  onBookSelect: (bookSlug: string, source?: string, collectionName?: string) => void;
  isAuthenticated: boolean;
  onStartReading: (bookSlug: string) => void;
  onPreloadBook: (bookId: string) => void;
  currentBook: Book | null;
  isPlaying: boolean;
  onTogglePlay: () => void;
  currentTime?: number;
  isReady?: boolean;
  isPositionRestored?: boolean;
  onBookFinished?: (bookId: string) => void;
  loading?: boolean;
  isLoading?: boolean;
}

function getEffectiveTimeForBook(args: {
  bookId: string;
  savedTime: number;
  cachedTime?: number;
  isCurrentBook: boolean;
  currentTime: number;
  isReady: boolean;
  isPositionRestored: boolean;
}) {
  const { savedTime, cachedTime, isCurrentBook, currentTime, isReady, isPositionRestored } = args;

  if (isCurrentBook && currentTime > 0 && isReady && isPositionRestored) return currentTime;
  if (cachedTime !== undefined) return cachedTime;
  return savedTime;
}

const EXIT_MS = 400;

const MyBooksPage: React.FC<MyBooksPageProps> = ({
  books,
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateToProfile,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onNavigateToMyBooks,
  onToggleMenu,
  onBookSelect,
  isAuthenticated,
  onStartReading,
  onPreloadBook,
  currentBook,
  isPlaying,
  onTogglePlay,
  currentTime = 0,
  isReady = false,
  isPositionRestored = false,
  onBookFinished,
  loading = false,
  isLoading = false,
}) => {
  const { theme } = useTheme();
  const { isLoading: authLoading, user } = useAuth();
  const isDarkMode = theme === 'dark';
  const breakpoint = useBreakpoint();

  const [activeFilter, setActiveFilter] = useState<FilterType>('All');
  const [isFilterTransitioning, setIsFilterTransitioning] = useState(false);
  const [listeningBooks, setListeningBooks] = useState<Book[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [savedProgress, setSavedProgress] = useState<Record<string, number>>({});
  const lastKnownProgressRef = useRef<Record<string, number>>({});
  const lastPlayedRef = useRef<Record<string, number>>({});
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [favourites, setFavourites] = useState<Record<string, boolean>>({});
  const favouritesRef = useRef<Record<string, boolean>>({});
  favouritesRef.current = favourites;
  const [hiddenBooks, setHiddenBooks] = useState<Record<string, boolean>>({});
  const hiddenBooksRef = useRef<Record<string, boolean>>({});
  hiddenBooksRef.current = hiddenBooks;
  const [openMenuBookId, setOpenMenuBookId] = useState<string | null>(null);
  // Bumped when server progress is hydrated into cookies, to re-read progress.
  const [progressSyncTick, setProgressSyncTick] = useState(0);

  // Conversation history state
  const [groupedHistory, setGroupedHistory] = useState<BookConversations[]>([]);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [usageError, setUsageError] = useState<string>('');
  const [selectedBookForConversations, setSelectedBookForConversations] = useState<BookConversations | null>(null);

  // Track cards being removed (used to trigger exit animation before actually removing from state)
  const [removing, setRemoving] = useState<Record<string, boolean>>({});

  const toggleMenu = useCallback((bookId: string) => {
    setOpenMenuBookId(prev => (prev === bookId ? null : bookId));
  }, []);

  const closeMenu = useCallback(() => setOpenMenuBookId(null), []);

  // Close popup when clicking outside
  useEffect(() => {
    const handlePopupClickOutside = (event: MouseEvent) => {
      const popup = document.getElementById('book-group-popup');
      if (selectedBookForConversations && popup && !popup.contains(event.target as Node)) {
        setSelectedBookForConversations(null);
      }
    };

    if (selectedBookForConversations) {
      document.addEventListener('mousedown', handlePopupClickOutside);
      // Disable background scrolling without shift
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Re-enable background scrolling
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.removeEventListener('mousedown', handlePopupClickOutside);
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [selectedBookForConversations]);

  // Load usage history. Held until auth settles: isAuthenticated is seeded from
  // stored credentials, so firing on it directly would request against a
  // session the server has not confirmed - and report a spurious
  // 'usage_history' Error Shown to Mixpanel when that session turns out to be
  // dead. Waiting also keeps this to one call per load, since the seeded user
  // object is replaced by the validated one.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !user) return;
    loadUsageHistory().catch(err => console.error('Error in loadUsageHistory effect:', err));
  }, [authLoading, isAuthenticated, user?.id, books]);

  const loadUsageHistory = async () => {
    try {
      setIsLoadingUsage(true);
      setUsageError('');
      const history = await authService.getUserUsageHistory();

      // Process history into grouped format
      processHistoryData(history);

    } catch (error) {
      console.error('Failed to load usage history:', error);
      const message = error instanceof Error ? error.message : 'Failed to load usage history';
      analytics.trackErrorShown('usage_history', message, {
        page: 'my_books',
        surface: 'my_books',
        operation: 'usage_history',
      });
      setUsageError(message);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const ITEMS_PER_PAGE = 15;

  const processHistoryData = (history: any[]) => {
    const groups: { [key: string]: BookConversations } = {};

    history.forEach(entry => {
      if (!entry.book_id) return;

      const bookId = entry.book_id;

      // Find the book by stable catalog identity.
      const book = books.find(b => b.id === bookId);

      // Use the found book's title or fallback to a formatted version of the ID
      const bookTitle = book ? book.title : bookId;
      const bookAuthor = book ? book.author : 'Unknown Author';
      const bookCoverUrl = book ? book.coverUrl : undefined;
      const bookSlug = book ? book.slug : bookId;

      if (!groups[bookId]) {
        groups[bookId] = {
          bookTitle,
          bookAuthor,
          bookSlug,
          bookCoverUrl,
          conversations: []
        };
      }

      // Calculate duration string
      const durationMins = entry.duration_minutes || 0;

      const mins = Math.floor(durationMins);
      const secs = Math.round((durationMins - mins) * 60);
      let durationStr: string;
      if (mins === 0) durationStr = `${secs} sec`;
      else if (secs === 0) durationStr = `${mins} min`;
      else durationStr = `${mins} min ${secs} sec`;

      groups[bookId].conversations.push({
        id: entry.id || `session-${Math.random()}`, // Fallback ID if missing
        date: entry.start || new Date().toISOString(),
        duration: durationStr,
        exchanges: entry.turns || 0
      });
    });

    // Sort conversations by date (newest first)
    Object.values(groups).forEach(group => {
      group.conversations.sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    });

    // Convert to array and sort by most recent conversation in the group
    const sortedGroups = Object.values(groups).sort((a, b) => {
      const dateA = a.conversations[0]?.date ? new Date(a.conversations[0].date).getTime() : 0;
      const dateB = b.conversations[0]?.date ? new Date(b.conversations[0].date).getTime() : 0;
      return dateB - dateA;
    });

    setGroupedHistory(sortedGroups);
  };

  const handleShowConversations = (bookId: string) => {
    const bookGroup = groupedHistory.find(g =>
      g.bookSlug === bookId || // Try slug match first
      books.find(b => b.id === bookId && b.slug === g.bookSlug) // Then try to resolve via books array
    );

    // If not found in history, construct an empty/initial group object if possible
    if (!bookGroup) {
      const book = books.find(b => b.id === bookId);
      if (book) {
         const newGroup: BookConversations = {
          bookTitle: book.title,
          bookAuthor: book.author,
          bookSlug: book.slug,
          bookCoverUrl: book.coverUrl,
          conversations: []
        };
        setSelectedBookForConversations(newGroup);
      }
    } else {
      setSelectedBookForConversations(bookGroup);
    }
  };

  const removeBookWithExit = useCallback((bookId: string) => {
    setRemoving(prev => ({ ...prev, [bookId]: true }));
    setTimeout(() => {
      setRemoving(prev => {
        const copy = { ...prev };
        delete copy[bookId];
        return copy;
      });
    }, EXIT_MS);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuBookId && !(event.target as Element).closest('.book-menu-container')) {
        setOpenMenuBookId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuBookId]);

  // Initialize listening books and saved progress
  useEffect(() => {
    const progressMap: Record<string, number> = {};
    const ratingsMap: Record<string, number> = {};
    const favouritesMap: Record<string, boolean> = {};
    const hiddenMap: Record<string, boolean> = {};

    // Map books to include their progress and lastPlayed timestamp
    const booksWithMetadata = books
      .map(book => {
        const savedPosition = getAudioProgressForBook(book);
        const progress = getEffectiveProgress(savedPosition);

        // Load rating and favourite status
        if (savedPosition?.rating) ratingsMap[book.id] = savedPosition.rating;
        if (savedPosition?.isFavourite) favouritesMap[book.id] = savedPosition.isFavourite;
        if (savedPosition?.isHidden) hiddenMap[book.id] = savedPosition.isHidden;

        // Use the latest timestamp from either the cookie or our local session ref
        const cookieLastPlayed = savedPosition?.lastPlayed || 0;
        const sessionLastPlayed = lastPlayedRef.current[book.id] || 0;
        const lastPlayed = Math.max(cookieLastPlayed, sessionLastPlayed);

        return { book, progress, lastPlayed };
      })
      // Include any book that has progress OR is favorited
      .filter(item => isBookInProgress(item.book, item.progress) || isBookFinished(item.book, item.progress) || favouritesMap[item.book.id]);

    // Handle current book
    if (currentBook) {
      const now = Date.now();
      const idx = booksWithMetadata.findIndex(item => item.book.id === currentBook.id);

      if (idx !== -1) {
        // Update lastPlayed to now for the current book so it stays at top
        booksWithMetadata[idx].lastPlayed = now;
      } else {
        // Add current book if not present (just started)
        booksWithMetadata.push({ book: currentBook, progress: 0, lastPlayed: now });
      }
      lastPlayedRef.current[currentBook.id] = now;
    }

    booksWithMetadata.sort((a, b) => b.lastPlayed - a.lastPlayed);

    const sortedBooks = booksWithMetadata.map(item => item.book);
    booksWithMetadata.forEach(item => {
      if (item.progress > 0) progressMap[item.book.id] = item.progress;
    });

    setSavedProgress(progressMap);
    setRatings(ratingsMap);
    setFavourites(favouritesMap);
    setHiddenBooks(hiddenMap);
    setListeningBooks(sortedBooks);
    setIsInitializing(false);
  }, [books, currentBook, progressSyncTick]); // Re-read when books/current book change, or after server progress syncs

  // Re-read cookies when cross-device server progress is hydrated.
  useEffect(() => {
    const handleProgressSynced = () => setProgressSyncTick(tick => tick + 1);
    window.addEventListener('aleph:progress-synced', handleProgressSynced);
    return () => window.removeEventListener('aleph:progress-synced', handleProgressSynced);
  }, []);

  // Update last known progress and last played time for the current book
  useEffect(() => {
    if (currentBook && currentTime > 0 && isReady && isPositionRestored) {
      lastKnownProgressRef.current[currentBook.id] = currentTime;
      lastPlayedRef.current[currentBook.id] = Date.now();
    }
  }, [currentTime, currentBook, isReady, isPositionRestored]);

  // Check if the current book has finished, animate it out
  useEffect(() => {
    if (!currentBook || !currentTime) return;

    // Check if book is finished (less than a quarter second remaining)
    const finished = currentBook.totalDuration - currentTime <= 0.125;

    // Only proceed if the book is in the listening list and not already being removed
    const isListening = listeningBooks.some(b => b.id === currentBook.id);

    // Only remove if we are in 'Unfinished' filter
    const shouldRemove = finished && isListening && !removing[currentBook.id] && activeFilter === 'Unfinished';

    if (shouldRemove) {
      // Remove after exit animation duration
      removeBookWithExit(currentBook.id);
    }
  }, [currentTime, currentBook, listeningBooks, removing, removeBookWithExit, activeFilter]);

  const resetProgressForPlayback = useCallback((book: Book) => {
    bookAnalytics.track('Book Progress Reset', book, {
      surface: 'my_books',
      trigger: 'button',
      input_method: 'unknown',
    });
    const resetPosition: AudioPosition = {
      sentenceIndex: -1,
      textFingerprint: '',
      timeOffset: 0,
      totalProgress: 0,
      lastPlayed: Date.now(),
      chapterId: undefined,
    };

    saveAudioProgress(book.id, resetPosition);
    setSavedProgress(prev => ({ ...prev, [book.id]: 0 }));
    lastKnownProgressRef.current[book.id] = 0;

    if (activeFilter === 'Finished') {
      removeBookWithExit(book.id);
    }
  }, [activeFilter, removeBookWithExit]);

  const toggleFavourite = useCallback((bookId: string) => {
    // Side effects (cookie write, analytics) live here rather than inside the
    // setFavourites updater below — React (in dev/StrictMode) invokes state
    // updaters twice to check for exactly this kind of impurity, which would
    // double-fire the "Book Favourite Status Changed" event.
    const next = !favouritesRef.current[bookId];
    const book = books.find(b => b.id === bookId);
    if (book) {
      saveBookFavourite(book.id, next);
        bookAnalytics.trackFavouriteStatusChange(book, {
          favourited: next,
          surface: 'my_books',
          trigger: 'button',
          input_method: 'unknown',
      });
    }
    setFavourites(prev => ({ ...prev, [bookId]: next }));
  }, [books]);

  const toggleHidden = useCallback((bookId: string, trigger: BookVisibilityTrigger) => {
    // See toggleFavourite above: side effects stay out of the updater so
    // StrictMode's double-invocation can't double-fire the analytics event.
    const next = !hiddenBooksRef.current[bookId];
    const book = books.find(b => b.id === bookId);
    if (book) {
      saveBookHidden(book.id, next);
      bookAnalytics.trackVisibilityChange(book, {
        hidden: next,
          surface: 'my_books',
          trigger,
          input_method: trigger === 'playback_start' ? 'automatic' : 'unknown',
      });
    }
    setHiddenBooks(prev => ({ ...prev, [bookId]: next }));
  }, [books]);

  const handleMarkFinished = useCallback((book: Book) => {
    bookAnalytics.trackCompletionStatusChange(book, {
      finished: true,
      trigger: 'manual',
      surface: 'my_books',
      input_method: 'unknown',
    });
    // If it's the current book, stop playback first
    if (currentBook && currentBook.id === book.id && onBookFinished) {
      onBookFinished(book.id);
    }

    // Delay cookie update slightly to ensure any pause-save logic in player has finished
    // and doesn't overwrite our finished status
    setTimeout(() => {
      // Save progress as finished (totalDuration)
      const finishedPosition: AudioPosition = {
        sentenceIndex: -1,
        textFingerprint: '',
        timeOffset: 0,
        totalProgress: book.totalDuration,
        lastPlayed: Date.now(),
        chapterId: undefined,
      };
      saveAudioProgress(book.id, finishedPosition);

      // Update local state to reflect finished status immediately
      setSavedProgress(prev => ({ ...prev, [book.id]: book.totalDuration }));

      // Update ref to ensure effectiveTime calculation uses the new finished time
      lastKnownProgressRef.current[book.id] = book.totalDuration;

      // Trigger exit animation IF we are in Unfinished filter
      if (activeFilter === 'Unfinished') {
          removeBookWithExit(book.id);
      }
    }, 50);
  }, [currentBook, onBookFinished, activeFilter, removeBookWithExit]);

  const handleMarkUnfinished = useCallback((book: Book) => {
    bookAnalytics.trackCompletionStatusChange(book, {
      finished: false,
      trigger: 'manual',
      surface: 'my_books',
      input_method: 'unknown',
    });
    // If it's the current book, stop playback first
    if (currentBook && currentBook.id === book.id && onBookFinished) {
      onBookFinished(book.id);
    }

    // Delay cookie update slightly to ensure any pause-save logic in player has finished
    setTimeout(() => {
      // Save progress as unfinished (0.1 seconds, so that the book remains in MyBooksPage)
      const unfinishedPosition: AudioPosition = {
        sentenceIndex: -1,
        textFingerprint: '',
        timeOffset: 0.1,
        totalProgress: 0.1,
        lastPlayed: Date.now(),
        chapterId: undefined,
      };
      saveAudioProgress(book.id, unfinishedPosition);

      // Update local state to reflect status immediately
      setSavedProgress(prev => ({ ...prev, [book.id]: 0.1 }));

      // Update ref
      lastKnownProgressRef.current[book.id] = 0;

      // Trigger exit animation IF we are in Finished filter
      if (activeFilter === 'Finished') {
          removeBookWithExit(book.id);
      }
    }, 50);
  }, [currentBook, onBookFinished, activeFilter, removeBookWithExit]);

  if (authLoading) return null;

  if (!isAuthenticated) {
    return (
      <LoginRequiredState
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToSignup={onNavigateToSignup}
        isLoading={isLoading}
        message="Please log in to view your books"
      />
    );
  }

  const filteredBooks = listeningBooks.filter(book => {
    // If manually removing (animating out), keep it in the list for now
    if (removing[book.id]) return true;

    const savedTime = savedProgress[book.id] || 0;
    const cachedTime = lastKnownProgressRef.current[book.id];
    const isCurrentBook = currentBook?.id === book.id;

    const effectiveTime = getEffectiveTimeForBook({
      bookId: book.id,
      savedTime,
      cachedTime,
      isCurrentBook,
      currentTime,
      isReady,
      isPositionRestored,
    });

    const isFav = favourites[book.id];
    const isHidden = hiddenBooks[book.id];
    const finished = isBookFinished(book, effectiveTime);
    const inProgress = isBookInProgress(book, effectiveTime);

    // If we are not in Hidden tab, exclude hidden books
    if (activeFilter !== 'Hidden' && isHidden) return false;

    // If we are in Hidden tab, only show hidden books
    if (activeFilter === 'Hidden') return isHidden;

    switch (activeFilter) {
      case 'Finished':
        return finished;
      case 'Unfinished':
        return inProgress;
      case 'Favourites':
        return isFav;
      case 'All':
      default:
        return true;
    }
  });

  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';

  const handleFilterClick = (value: FilterType) => {
    if (value !== activeFilter) {
      setIsFilterTransitioning(true);
      setActiveFilter(value);
      setTimeout(() => setIsFilterTransitioning(false), 50);
    }
  };

  return (
    <div
      className={`w-full ${themeClasses} transition-colors duration-300`}
      style={{
        backgroundColor: isDarkMode
          ? 'var(--background-colour-dark-mode)'
          : 'var(--background-colour-light-mode)'
      }}
    >
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToMyBooks={onNavigateToMyBooks}
        onNavigateToProfile={onNavigateToProfile}
        onBookSelect={onBookSelect}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        showProfileIcon={isAuthenticated}
        isLoading={isLoading}
        activeItem="myBooks"
      />

      {/* Conversations Popup */}
      <AnimatePresence>
        {selectedBookForConversations && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-hidden"
            style={{ touchAction: 'none' }}
          >
            <motion.div
              id="book-group-popup"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={`w-full max-w-2xl flex flex-col rounded-xl shadow-2xl overflow-hidden ${
                selectedBookForConversations.conversations.length > ITEMS_PER_PAGE ? 'h-[80vh]' : 'max-h-[80vh]'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <BookGroup
                book={selectedBookForConversations}
                isDarkMode={isDarkMode}
                isPopup={true}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full px-4 xs:px-6 sm:px-8 md:px-12 lg:px-16 xl:px-24 pt-12 pb-[calc(10rem+var(--safe-bottom))]">
        <div className="w-full max-w-6xl mx-auto">
          <div className="flex flex-col gap-6 sm:gap-10 mb-4">
            <h1 className={`portrait-text-app text-left font-bold text-4xl sm:text-5xl lg:text-6xl ${
              isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
              My Books
            </h1>

            <div className="flex flex-wrap gap-1 2xs:gap-2">
              <FilterButton label="All" value="All" activeFilter={activeFilter} isDarkMode={isDarkMode} onClick={handleFilterClick} />
              <FilterButton label="Finished" value="Finished" activeFilter={activeFilter} isDarkMode={isDarkMode} onClick={handleFilterClick} />
              <FilterButton label="Unfinished" value="Unfinished" activeFilter={activeFilter} isDarkMode={isDarkMode} onClick={handleFilterClick} />
              <FilterButton label="Favourites" value="Favourites" activeFilter={activeFilter} isDarkMode={isDarkMode} onClick={handleFilterClick} />
              <FilterButton label="Hidden" value="Hidden" activeFilter={activeFilter} isDarkMode={isDarkMode} onClick={handleFilterClick} />
            </div>
          </div>

          <div key={activeFilter}>
            {loading || isInitializing ? (
              <div className="flex justify-center py-12" />
            ) : filteredBooks.length === 0 ? (
              <div className={`text-center py-12 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                <p className="text-lg 2xs:text-xl">
                   {activeFilter === 'All'
                     ? "You haven't started listening to any books yet."
                     : activeFilter === 'Favourites'
                       ? "You haven't favourited any books yet."
                       : activeFilter === 'Finished'
                         ? "You haven't finished any books yet."
                         : activeFilter === 'Hidden'
                           ? "You have no hidden books."
                           : "You have no unfinished books."}
                </p>
                {activeFilter === 'All' && (
                  <button
                    onClick={onNavigateToLibrary}
                    className="mt-4 px-6 py-2 text-sm 2xs:text-base bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                  >
                    Browse Library
                  </button>
                )}
              </div>
            ) : (
              <div className={`flex flex-col border-y ${isDarkMode ? 'border-gray-700/70 divide-y divide-gray-700/70' : 'border-gray-300 divide-y divide-gray-300'}`}>
                <LayoutGroup>
                  <AnimatePresence initial={false} mode="popLayout">
                  {filteredBooks
                    .map(book => (
                      <MyBookItem
                        key={book.id}
                        book={book}
                        isCurrentBook={currentBook?.id === book.id}
                        isPlaying={isPlaying}
                        isReady={isReady}
                        isPositionRestored={isPositionRestored}
                        currentTime={currentTime}
                        savedTime={savedProgress[book.id] || 0}
                        cachedTime={lastKnownProgressRef.current[book.id]}
                        isDarkMode={isDarkMode}
                        breakpoint={breakpoint}
                        isFav={favourites[book.id] || false}
                        currentRating={ratings[book.id] || 0}
                        menuIsOpen={openMenuBookId === book.id}
                        removing={removing[book.id]}
                        isFilterTransitioning={isFilterTransitioning}
                        onBookSelect={(slug) => onBookSelect(slug, 'my_books')}
                        onTogglePlay={onTogglePlay}
                        onStartReading={onStartReading}
                        onPreloadBook={onPreloadBook}
                        toggleMenu={toggleMenu}
                        toggleFavourite={toggleFavourite}
                        closeMenu={closeMenu}
                        handleMarkFinished={handleMarkFinished}
                        handleMarkUnfinished={handleMarkUnfinished}
                        handleShowConversations={handleShowConversations}
                        resetProgressForPlayback={resetProgressForPlayback}
                        isHidden={hiddenBooks[book.id]}
                        toggleHidden={toggleHidden}
                      />
                    ))}
                </AnimatePresence>
              </LayoutGroup>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyBooksPage;
